import React, { useEffect, useState } from 'react'
import { Tag, Button, Spin, Popconfirm } from 'antd'
import { EditOutlined, DeleteOutlined, RightOutlined } from '@ant-design/icons'
import { useDeleteBlog } from 'api/Blogs/Blogs'
import { useQueryClient } from '@tanstack/react-query'
import { formatDateTime } from 'utils/dateUtils'
import Loader from 'helper/Loader'
import './BlogCardView.scss'
import { stripHtmlTags } from 'helper/utility'
import { getBlogStatusColors } from 'helper/blogStatusColors'
import { Visibility } from 'types/blogs/blogs'
import dummy from '../../../assets/Images/No-image-found.jpeg'

interface BlogCardViewProps {
  blogs: any
  blogsLoading: boolean
  onEdit?: (blogId: string) => void
  onPreview?: (blog: any) => void
  refetch?: () => void
}

const BlogCardView = ({
  blogs,
  blogsLoading,
  onEdit,
  onPreview,
  refetch,
}: BlogCardViewProps) => {
  const queryClient = useQueryClient()
  const { mutate: deleteBlogPost, isLoading } = useDeleteBlog()

  const blogPosts = blogs?.records || []

  //  Active Featured Blog State
  const [activeBlog, setActiveBlog] = useState<any>(null)

  // Set first blog as default featured
  useEffect(() => {
    if (blogPosts.length > 0) {
      setActiveBlog(blogPosts[0])
    }
  }, [blogs])

  const handleDelete = (id: string) => {
    deleteBlogPost(id, {
      onSuccess: () => {
        queryClient.invalidateQueries(['all-blogs'])
        refetch?.()
      },
    })
  }

  const StatusFormat = (status: string) => {
    if (status === 'public') return 'PUBLIC'
    if (status === 'draft') return 'DRAFT'
    return status
  }

  const getStatusClass = (visibility: string) => {
    if (visibility === 'public') return 'status-public'
    if (visibility === 'draft') return 'status-draft'
    return 'status-default'
  }

  // Remove active blog from bottom list
  const otherBlogs = blogPosts.filter(
    (blog: any) => blog._id !== activeBlog?._id
  )
  const dateTime = formatDateTime(activeBlog?.createdAt);
  const parts = dateTime.split(',');


  if (blogsLoading) {
    return (
      <div className="blog-loader">
        <Loader />
      </div>
    )
  }

  return (
    <div className="blog-card-view">
      {isLoading && (
        <div className="overlay-loader">
          <Loader />
        </div>
      )}

      {/* ================= FEATURED BLOG ================= */}
      {activeBlog && (
        <div className="featured-blog" onClick={() => onPreview?.(activeBlog)}>
          <div className="featured-image">
            <img
              src={activeBlog.featureImage?.original ? activeBlog.featureImage?.original : dummy}
              alt={activeBlog.title}
            />
          </div>

          <div className="featured-content">
            <h2>{activeBlog.title}</h2>

            <p className="featured-excerpt">
              {stripHtmlTags(activeBlog.excerpt) || 'No description available'}
            </p>

            <div className="featured-meta">
              <Tag
                className={`status-tag ${getStatusClass(
                  activeBlog.visibility
                )}`}
                style={{
                  backgroundColor: getBlogStatusColors(activeBlog.visibility).backgroundColor,
                  color: getBlogStatusColors(activeBlog.visibility).color,
                  border: "none",
                  borderRadius: '15px',
                  padding: '4px 10px',
                  fontWeight: 'bold',
                }}
              >
                {activeBlog.visibility?.toUpperCase() || 'UNKNOWN'}
              </Tag>

              <span style={{ color: '#000000', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>
                {parts[0]}
                <span style={{ color: '#2BA7CA', fontWeight: '700' }}>
                  {parts[1] ? `${parts[1]}` : ''}
                </span>
              </span>
            </div>

            <span
              className="read-more"
              onClick={() => onPreview?.(activeBlog)}
            >
              Read more &nbsp; <RightOutlined />
            </span>
          </div>
        </div>
      )}

      {/* ================= OTHER BLOGS ================= */}
      <div className="other-blogs">
        {otherBlogs.map((blog: any) => (
          <div key={blog._id} className="blog-card">
            <div
              className="card-clickable"
              onClick={() => onPreview?.(blog)}
            >
              <div className="card-image">
                <img
                  src={blog.featureImage?.original ? blog.featureImage?.original : dummy}
                  alt={blog.title}
                />
              </div>

              <div className="card-content">
                <h4>{blog.title}</h4>
                <div className="card-meta">
                  <span style={{ color: '#000000', cursor: 'pointer', fontWeight: '400', fontSize: '14px' }}>
                    {parts[0]}
                    <span style={{ color: '#2BA7CA', fontWeight: '700' }}>
                      {parts[1] ? `${parts[1]}` : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="card-actions">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => onEdit?.(blog._id)}
              />

              {blog?.visibility !== Visibility.ARCHIVED && (
                <Popconfirm
                  title="Delete this blog?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleDelete(blog._id)}
                >
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              )}
            </div>
          </div>
        ))}
      </div>

      {blogPosts.length === 0 && (
        <div className="empty-state">
          No blog posts found
        </div>
      )}
    </div>
  )
}

export default BlogCardView
